import { CurrentUser } from '@qualio/ui-components/lib/types/CurrentUser';
import axios, { AxiosProgressEvent, CancelToken, HttpStatusCode } from 'axios';
import {
  API_ATTACHMENT_EXTERNAL_SOURCE_ERROR,
  API_ATTACHMENT_UPLOAD_ERROR,
  logCustomError,
} from '../messages/LogErrorMessages';
import { DocumentPropertiesDTO } from '../types/DocumentCreate';
import {
  ResolvedSmartLinkInformation,
  SmartLinksSuggest,
} from '../util/SmartLinkUtils';
import { ModifiedRemoteFileMetadata } from '../views/components/QFilePicker/index';
import { Question } from '../views/components/TrainingAssessment/types/trainingAssessment';
import { medtechApiClient } from './index';
import { ActivityLog } from './model/activityLog';
import {
  DocumentAccount,
  DocumentPreviewResponse,
  DocumentStatus,
  DocumentTemplateUpgradeIdList,
  DocumentTemplateUpgradeInfo,
  DocumentVersion,
  NextVersionDetails,
  PreviewItems,
  QualioDocument,
  QualioDocumentAttachment,
  QualioDocumentPatch,
  QualioDocumentStatus,
  SmartLinkedDocumentSuggestion,
} from './model/document';
import {
  CreatePeriodicReviewRequest,
  PeriodicReview,
  PeriodicReviewExtended,
} from './model/periodicReview';
import { Tag } from './model/tag';
import { MedtechUserV2 } from './user';

export type FetchAllResult = { documents: QualioDocument[]; total: number };

export type DocumentApiQueryParams = {
  status_id?: string[] | null;
  query: string | null;
  tag_id: string[];
  template_code?: string[];
  offset?: number | undefined;
  limit?: number | undefined;
  max_age_days?: number;
  current_major_version_only?: boolean;
  include_pending_review?: boolean;
  include_pending_periodic_review?: boolean;
  order_by?: string | null | undefined;
  owner_id?: number | null;
  account?: DocumentAccount;
};

export type UserCredentialsPayload = {
  email?: string;
  password?: string;
  comment?: string;
};

export type ChangeStatusPayload = UserCredentialsPayload & {
  status: QualioDocument['status'];
};

export type ChangeOrientationPayload = {
  orientation: QualioDocument['orientation'];
};

export type SuppressedAssignedTraineesToDocuments = {
  id: QualioDocument['id'];
  code: QualioDocument['code'];
  suppressed: {
    current_trainee?: Pick<MedtechUserV2, 'email' | 'full_name' | 'id'>[];
    document_inaccessible?: Pick<MedtechUserV2, 'email' | 'full_name' | 'id'>[];
  };
};

export type AssignTraineesToDocumentsResponse = {
  done: Pick<MedtechUserV2, 'email' | 'full_name' | 'id'> &
    { notified_about: number }[];
  suppressed: SuppressedAssignedTraineesToDocuments[];
};

export type ChangeStatusResponse = Pick<
  QualioDocument,
  | 'company_id'
  | 'id'
  | 'major_version'
  | 'minor_version'
  | 'status'
  | 'status_id'
  | 'title'
>;

export type ChangeOrientationResponse = Pick<
  QualioDocument,
  'id' | 'orientation'
>;

interface DocumentApiOptions {
  params: DocumentApiQueryParams;
  paramsSerializer: {
    indexes: null;
  };
}

export type DocumentUpdateParams = {
  update_content_last_modified?: number;
  create_section_edit_activity_log?: boolean;
};

export type TrainingAssessmentValidation = {
  documentId: number;
  questions: Question[];
  id: number;
  percentage_of_answers_correct?: number;
};

export type GeneralActionTypeFilter =
  | 'all-activities'
  | 'lifecycle'
  | 'modifications';

const buildActionableDocumentsStatusQueryParams = (statusFilters: string[]) => {
  return statusFilters.reduce(
    (acc: { [key: string]: boolean }, val: string) => {
      acc[val] = true;
      return acc;
    },
    {},
  );
};

export const getOptionsWithStatusFiltersForActionableDocs = (
  statusFilters: string[],
  options: DocumentApiOptions,
) => {
  options.params = {
    ...options.params,
    ...buildActionableDocumentsStatusQueryParams(statusFilters),
  };
  return options;
};

const getActionableDocsOptions = (
  searchQuery: string | null,
  tagIds: string[],
  orderBy: string | null | undefined,
  offset?: number | undefined,
  limit?: number | undefined,
  statusFilters?: string[] | null | undefined,
  templatesCodes?: string[] | null | undefined,
  ownerId: number | null = null,
) => {
  const params: DocumentApiQueryParams = {
    offset,
    limit,
    query: searchQuery,
    tag_id: tagIds,
    order_by: orderBy,
  };

  if (templatesCodes) {
    params.template_code = templatesCodes;
  }

  if (ownerId !== null) {
    params.owner_id = ownerId;
  }

  const options = {
    params,
    paramsSerializer: {
      indexes: null,
    },
  };

  if (statusFilters === null || statusFilters === undefined) {
    return options;
  }

  return getOptionsWithStatusFiltersForActionableDocs(statusFilters, options);
};

interface StatusFilterParams {
  max_age_days?: number;
  current_major_version_only?: boolean;
  include_pending_review?: boolean;
  include_pending_periodic_review?: boolean;
  status_id?: string[] | null;
}

const isFilteringByDeletedStatusOnly = (statusFilters: string[] | null) => {
  return (
    statusFilters?.length === 1 && statusFilters[0] === DocumentStatus.Deleted
  );
};

export const getStatusFilterParamsForDocs = (
  statusFilters: string[] | null,
  current_major_version_only: boolean,
) => {
  const params: StatusFilterParams = {};
  if (isFilteringByDeletedStatusOnly(statusFilters)) {
    params.max_age_days = 90;
    if (current_major_version_only) {
      params.current_major_version_only = true;
    }
  }
  if (
    statusFilters?.length === 1 &&
    statusFilters[0] === DocumentStatus.For_Periodic_Review
  ) {
    params.include_pending_periodic_review = true;
  } else {
    params.status_id = statusFilters;
  }

  return params;
};

const getFetchDocsParamsObject = (
  statusFilters: string[] | null,
  offset: number,
  limit: number,
  searchQuery: string | null,
  tagIds: string[],
  orderBy: string | null | undefined,
  templatesCodes: string[] | null | undefined,
  current_major_version_only = false,
  ownerId: number | null = null,
  account?: DocumentAccount,
) => {
  const params: DocumentApiQueryParams = {
    offset,
    limit,
    query: searchQuery,
    tag_id: tagIds,
    order_by: orderBy,
  };

  if (templatesCodes) {
    params.template_code = templatesCodes;
  }

  if (ownerId !== null) {
    params.owner_id = ownerId;
  }

  if (account !== undefined) {
    params.account = account;
  }

  if (isFilteringByDeletedStatusOnly(statusFilters)) {
    params.query = null;
  }

  return {
    params: {
      ...params,
      ...getStatusFilterParamsForDocs(
        statusFilters,
        current_major_version_only,
      ),
    },
    paramsSerializer: {
      indexes: null,
    },
  };
};

export const documentApi = {
  async fetch(
    id: string,
    process_references = 1,
    include_export_data = 0,
  ): Promise<QualioDocument> {
    return medtechApiClient
      .get(
        `/documents/${id}?include_attachments=true&process_references=${process_references}&include_export_data=${include_export_data}`,
      )
      .then((res) => res.data);
  },
  async fetchAll(
    currentUser: CurrentUser,
    statusFilters: string[] | null,
    searchQuery: string | null,
    tagIds: string[],
    offset: number,
    limit: number,
    orderBy: string | null | undefined,
    templatesCodes: string[] | null | undefined,
    ownerId: number | null = null,
    account?: DocumentAccount,
  ): Promise<FetchAllResult> {
    const exportTaskUrl = `v3/${currentUser.companyId}/documents`;
    const params = getFetchDocsParamsObject(
      statusFilters,
      offset,
      limit,
      searchQuery,
      tagIds,
      orderBy,
      templatesCodes,
      false,
      ownerId,
      account,
    );
    return medtechApiClient.get(exportTaskUrl, params).then((res) => res.data);
  },
  async fetchFirstParentCompanyDocument(
    currentUser: CurrentUser,
    statusFilters: string[],
  ): Promise<QualioDocument | undefined> {
    return medtechApiClient
      .get<FetchAllResult>(`v3/${currentUser.companyId}/documents`, {
        params: {
          status_id: statusFilters,
          offset: 0,
          limit: 1,
          account: DocumentAccount.Parent,
        },
        paramsSerializer: { indexes: null },
      })
      .then((res) => res.data.documents[0]);
  },
  async fetchUserDocs(
    currentUser: CurrentUser,
    statusFilters: string[] | null,
    searchQuery: string | null,
    tagIds: string[],
    offset: number,
    limit: number,
    orderBy: string | null | undefined,
    templatesCodes: string[] | null | undefined,
    ownerId: number | null = null,
  ): Promise<FetchAllResult> {
    const current_major_version_only = true;
    const exportTaskUrl = `v3/${currentUser.companyId}/users/${currentUser.userId}/documents`;
    const params = getFetchDocsParamsObject(
      statusFilters,
      offset,
      limit,
      searchQuery,
      tagIds,
      orderBy,
      templatesCodes,
      current_major_version_only,
      ownerId,
    );
    return medtechApiClient.get(exportTaskUrl, params).then((res) => res.data);
  },
  async fetchUserActionableDocs(
    currentUser: CurrentUser,
    statusFilters: string[] | null,
    searchQuery: string | null,
    tagIds: string[],
    offset: number,
    limit: number,
    orderBy: string | null | undefined,
    templatesCodes: string[] | null | undefined,
    ownerId: number | null = null,
  ): Promise<FetchAllResult> {
    const options = getActionableDocsOptions(
      searchQuery,
      tagIds,
      orderBy,
      offset,
      limit,
      statusFilters,
      templatesCodes,
      ownerId,
    );

    return medtechApiClient
      .get(
        `v3/${currentUser.companyId}/users/${currentUser.userId}/actionable_documents`,
        options,
      )
      .then((res) => res.data);
  },
  async fetchUserTrainingDocs(
    currentUser: CurrentUser,
    statusFilters: string[] | null,
    searchQuery: string | null,
    tagIds: string[],
    offset?: number | undefined,
    limit?: number | undefined,
    orderBy?: string | null | undefined,
    templatesCodes?: string[] | null | undefined,
    ownerId: number | null = null,
  ): Promise<FetchAllResult> {
    const options = getActionableDocsOptions(
      searchQuery,
      tagIds,
      orderBy,
      offset,
      limit,
      statusFilters,
      templatesCodes,
      ownerId,
    );
    return medtechApiClient
      .get(
        `v2/${currentUser.companyId}/users/${currentUser.userId}/training`,
        options,
      )
      .then((res) => res.data);
  },
  async fetchSpecificVersion(
    id: string,
    version: string,
  ): Promise<QualioDocument> {
    return medtechApiClient
      .get(
        `/documents/${id}/versions/${version}?include_sections=true&include_attachments=true&process_references=1`,
      )
      .then((res) => res.data);
  },
  async fetchNextVersion(
    documentId: number,
    companyId: number,
  ): Promise<NextVersionDetails> {
    return medtechApiClient
      .get(`v2/${companyId}/documents/${documentId}/next_version_details`)
      .then((res) => res.data);
  },
  async fetchDocumentExportPayload(
    id: string,
    companyId: number,
  ): Promise<{
    exports: { html: string; header: { title: string; top_right: string } }[];
  }> {
    return medtechApiClient
      .get(`v3/${companyId}/documents/export-payload?document_id=${id}`)
      .then((res) => res.data);
  },
  async fetchByIdOrVersion(
    id: string,
    version: string,
    process_references = 1,
    include_export_data = 0,
  ): Promise<QualioDocument> {
    if (version) {
      return documentApi.fetchSpecificVersion(id, `${version}`);
    }
    return documentApi.fetch(id, process_references, include_export_data);
  },
  async fetchStatus(id: string): Promise<QualioDocumentStatus> {
    return medtechApiClient
      .get(`documents/${id}/status`)
      .then((res) => res.data);
  },
  async update(
    payload: QualioDocumentPatch,
    documentId: number,
    shouldCreateSectionEditActivityLog = false,
    updateModifiedTime = true,
  ): Promise<QualioDocument> {
    const params: DocumentUpdateParams = {};
    const url = `/documents/${documentId}`;
    if (!updateModifiedTime) {
      params.update_content_last_modified = 0;
    } else if (shouldCreateSectionEditActivityLog) {
      params.create_section_edit_activity_log = true;
    }
    return medtechApiClient
      .patch(url, payload, {
        params: params,
        paramsSerializer: {
          indexes: null,
        },
      })
      .then((res) => res.data);
  },
  async fetchAttachmentToken(companyId: number): Promise<string> {
    return medtechApiClient
      .get(`/${companyId}/attachment-token`)
      .then((res) => res.data);
  },
  async fetchAttachmentInfo(id: string): Promise<QualioAttachmentInfoResponse> {
    return medtechApiClient
      .get(`/attachmentinfo/${id}`)
      .then((res) => res.data);
  },
  async fetchAttachmentDataAsBase64(id: string): Promise<string> {
    return medtechApiClient
      .get(`/attachmentdata/${id}`)
      .then((res) => res.data);
  },
  async fetchAttachmentDataAsBlob(id: string, mimeType: string): Promise<Blob> {
    return medtechApiClient
      .get(`/attachments/${id}`, { responseType: 'blob' })
      .then(
        (res) =>
          new Blob([res.data], {
            type: mimeType,
          }),
      );
  },

  async deleteDocument(documentId: number): Promise<void> {
    await medtechApiClient.delete(`/documents/${documentId}`);
  },
  async uploadAttachment(
    file: File,
    setProgress: (progress: AxiosProgressEvent) => void,
    cancelToken: CancelToken,
    formData?: { [key: string]: string },
    entityId?: number,
    entityType?: string,
  ): Promise<QualioDocumentAttachment | undefined> {
    const data = new FormData();
    data.append('file_1', file);
    if (formData) {
      for (const key in formData) {
        data.append(key, formData[key]);
      }
    }
    let httpResp: any;
    let uploadUrl = '/attachments';
    if (entityId && entityType) {
      uploadUrl += `?entity=${entityType}&entity_id=${entityId}`;
    }
    try {
      httpResp = await medtechApiClient.post(uploadUrl, data, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: setProgress,
        cancelToken,
      });
    } catch (error: any) {
      if (!axios.isCancel(error)) {
        logCustomError(API_ATTACHMENT_UPLOAD_ERROR, { error, entityId });
        throw error;
      }
    }
    return httpResp?.data;
  },
  async addExternalSourceFileAsAttachment(
    entityId: number,
    companyId: number,
    modifiedRemoteFileMetadata: ModifiedRemoteFileMetadata,
  ): Promise<QualioDocumentAttachment> {
    const addUrl = `${companyId}/attachment/from-external-source`;
    let httpResp: any;
    const data = {
      remote_file_metadata: {
        download_url: modifiedRemoteFileMetadata.downloadUrl,
        web_url: modifiedRemoteFileMetadata.webUrl,
        source: 'one_drive',
        presigned_url: modifiedRemoteFileMetadata.presignedUrl,
        filename: modifiedRemoteFileMetadata.filename,
        last_modified_at: modifiedRemoteFileMetadata.lastModifiedAt,
      },
      entity_id: entityId,
      entity: 'document.section',
    };
    try {
      httpResp = await medtechApiClient.post(addUrl, data);
    } catch (error: any) {
      if (!axios.isCancel(error)) {
        logCustomError(API_ATTACHMENT_EXTERNAL_SOURCE_ERROR, {
          error,
          entityId,
        });
        throw error;
      }
    }
    return httpResp?.data;
  },
  async smartLinksSuggest(
    companyId: number,
    payload: SmartLinksSuggest,
  ): Promise<SmartLinkedDocumentSuggestion[]> {
    const httpResp: any = await medtechApiClient.get(
      `/v2/${companyId}/smartlinks/suggest?search=${payload.search}`,
    );
    return httpResp.data;
  },
  async smartLinksResolve(
    companyId: number,
    codes: Set<string>,
  ): Promise<ResolvedSmartLinkInformation[]> {
    const codesAsQueryParam = '?codes=' + [...codes].join(',');
    const httpResp: any = await medtechApiClient.get(
      `/v2/${companyId}/smartlinks/resolve-bulk${codesAsQueryParam}`,
    );
    return httpResp.data;
  },
  async smartLinksMatrixResolve(
    companyId: number,
    matrixIds: Set<string>,
  ): Promise<ResolvedSmartLinkInformation[]> {
    const matrixIdsAsQueryParam = '?ids=' + [...matrixIds].join(',');
    const httpResp: any = await medtechApiClient.get(
      `/v2/${companyId}/smartlinks/resolve-bulk-matrix${matrixIdsAsQueryParam}`,
    );
    return httpResp.data;
  },
  async deleteDocumentCollaborationSession(
    companyId: number,
    documentId: number,
    force: boolean,
  ) {
    const params = new URLSearchParams({ force: force.toString() });
    const httpResp: any = await medtechApiClient.delete(
      `/v2/${companyId}/collaboration/sessions/document/${documentId}?${params.toString()}`,
    );
    return httpResp.data;
  },
  async fetchCommentThreads(
    companyId: number,
    documentId: string,
    context?: 'document' | 'change-control',
  ): Promise<CommentsRepository.CommentThreadFetched[]> {
    const url = `/v2/${companyId}/documents/${documentId}/comment_threads`;
    let httpResp;
    if (context) {
      httpResp = await medtechApiClient.get(url, {
        params: {
          entity: context,
        },
      });
    } else {
      httpResp = await medtechApiClient.get(url);
    }
    return httpResp.data;
  },
  async setDeletedByUserCommentThreads(
    companyId: number,
    documentId: number,
    threadId: string,
    channelId: string,
    commentId?: string,
  ): Promise<any> {
    const payLoad: Record<string, string> = {
      channelId: channelId,
      action: 'add_deleted_by_user',
    };
    if (commentId) {
      payLoad.commentId = commentId;
    }
    const httpResp = await medtechApiClient.patch(
      `/v2/${companyId}/documents/${documentId}/comment_threads/${threadId}`,
      payLoad,
    );

    return httpResp.data;
  },
  async updateSuggestionAttributes(
    companyId: number,
    documentId: string,
    suggestionId: string,
    channelId: string,
    attributes: Map<string, any>,
  ): Promise<any> {
    const payLoad = {
      channelId: channelId,
      attributes: Object.fromEntries(attributes),
      action: 'add_attributes',
    };
    const httpResp = await medtechApiClient.patch(
      `/v2/${companyId}/documents/${documentId}/suggestions/${suggestionId}`,
      payLoad,
    );

    return httpResp.data;
  },
  async convertToQualioDocument(
    doc: QualioDocument,
    shouldExtractContent?: boolean,
  ): Promise<any> {
    const { company_id, id } = doc;
    const httpResp = await medtechApiClient.post(
      `/v2/${company_id}/documents/${id}/convert_document_format`,
      undefined,
      shouldExtractContent
        ? { params: { extract_content: shouldExtractContent } }
        : undefined,
    );

    return httpResp.data;
  },
  async fetchNextAvailableDocumentCode(
    companyId: number,
    templateId: number,
  ): Promise<string> {
    const httpResp = await medtechApiClient.get<string>(
      `/v2/${companyId}/template/${templateId}/next_document_code`,
    );
    return httpResp.data;
  },
  async convertToFileDocument(
    companyId: number,
    documentId: number,
    attachmentId?: number,
  ) {
    const baseEndpoint = `v2/${companyId}/documents/${documentId}/convert_document_attachment`;
    const endpoint = attachmentId
      ? `${baseEndpoint}/${attachmentId}`
      : baseEndpoint;
    return medtechApiClient.post(endpoint);
  },
  async createDraftFromEffectiveDoc(
    companyId: number,
    documentMatrixId: string,
  ) {
    const payload = {
      company_id: companyId,
      document_matrix_id: documentMatrixId,
    };
    const httpResp = await medtechApiClient.post('/documents', payload);

    return httpResp.data;
  },
  async createDraft(companyId: number, document: DocumentPropertiesDTO) {
    const payload = {
      ...document,
      company_id: companyId,
    };
    const httpResp = await medtechApiClient.post('/documents', payload);
    return httpResp.data;
  },
  async changeStatus(
    documentId: number,
    payload: ChangeStatusPayload,
  ): Promise<ChangeStatusResponse> {
    return medtechApiClient
      .put(`/documents/${documentId}/change-status`, payload)
      .then(({ data }) => data);
  },
  async review(documentId: number): Promise<ChangeStatusResponse> {
    return medtechApiClient
      .put(`/documents/${documentId}/review`)
      .then(({ data }) => data);
  },
  async sendForSequentialApproval(
    companyId: number,
    documentId: number,
  ): Promise<ChangeStatusResponse> {
    return medtechApiClient
      .put(`/v2/${companyId}/documents/${documentId}/for-approval`)
      .then(({ data }) => data);
  },
  async revertSequentialToDraft(
    companyId: number,
    documentId: number,
    comment?: string,
  ): Promise<ChangeStatusResponse> {
    return medtechApiClient
      .put(`/v2/${companyId}/documents/${documentId}/revert-to-draft`, {
        comment,
      })
      .then(({ data }) => data);
  },
  async editorDone(
    companyId: number,
    documentId: number,
  ): Promise<ChangeStatusResponse> {
    return medtechApiClient
      .put(`/${companyId}/documents/${documentId}/editor-done`)
      .then(({ data }) => data);
  },
  async applyTagChangesToDocuments(
    documents: QualioDocument['id'][],
    addedTagIds: Tag['id'][],
    removedTagIds: Tag['id'][],
    force = false,
  ) {
    const payload = {
      documents,
      add_tags: addedTagIds,
      remove_tags: removedTagIds,
      force,
    };
    return medtechApiClient.post('documents/tags/', payload);
  },
  async assignTraineesToDocuments(
    currentUser: CurrentUser,
    documentIds: QualioDocument['id'][],
    userIds: MedtechUserV2['id'][],
  ) {
    return medtechApiClient
      .post<AssignTraineesToDocumentsResponse>(
        `/v2/${currentUser.companyId}/assign_training`,
        {
          document_ids: documentIds,
          user_ids: userIds,
        },
      )
      .then(({ data }) => data);
  },
  async getDocumentUpgradeInformation(
    companyId: number,
    documentId: number,
  ): Promise<DocumentTemplateUpgradeInfo> {
    return medtechApiClient
      .get<DocumentTemplateUpgradeInfo>(
        `/v2/${companyId}/documents/${documentId}/check_upgradable`,
      )
      .then(({ data }) => data);
  },
  async createDocumentUpgradePreview(
    companyId: number,
    documentId: number,
    target_template_id: number,
    useNewTemplateUpgrade = false,
  ): Promise<PreviewItems> {
    const endpointVersion = useNewTemplateUpgrade ? 'v3' : 'v2';
    return medtechApiClient
      .post<DocumentPreviewResponse>(
        `/${endpointVersion}/${companyId}/document_templates/upgrade_documents`,
        {
          documents_ids: [documentId],
          preview: true,
          target_template_id: target_template_id,
        },
      )
      .then(({ data }) => {
        const { preview_document_sections, ...rest } = data;
        return {
          preview_result_per_version_change: preview_document_sections,
          ...rest,
        };
      });
  },
  async upgradeDocument(
    companyId: number,
    documentId: number,
    target_template_id: number,
    useNewTemplateUpgrade = false,
  ): Promise<QualioDocument['id'][]> {
    const endpointVersion = useNewTemplateUpgrade ? 'v3' : 'v2';
    return medtechApiClient
      .post<DocumentTemplateUpgradeIdList>(
        `/${endpointVersion}/${companyId}/document_templates/upgrade_documents`,
        {
          documents_ids: [documentId],
          target_template_id: target_template_id,
        },
      )
      .then(({ data }) => data.updated_documents_ids);
  },
  updateDocumentTrainees(
    documentId: QualioDocument['id'],
    userIds: MedtechUserV2['id'][],
    specificDocumentUpdate?: boolean,
  ) {
    return medtechApiClient
      .patch(
        `/documents/${documentId}/training_group`,
        { users: userIds },
        specificDocumentUpdate
          ? { params: { specific_document_update: true } }
          : undefined,
      )
      .then(({ data }) => data);
  },
  getPeriodicReviewByDocumentId(
    currentUser: CurrentUser,
    documentId: QualioDocument['id'],
  ) {
    return medtechApiClient
      .get<PeriodicReview>(
        `/v2/${currentUser.companyId}/documents/${documentId}/periodic_review`,
      )
      .then((res) => {
        if (res.status === HttpStatusCode.NoContent) {
          return undefined;
        } else {
          return res.data;
        }
      });
  },
  async getPeriodicReviewByReviewId(
    companyId: number,
    id: number,
  ): Promise<PeriodicReviewExtended> {
    const response = await medtechApiClient.get<PeriodicReviewExtended>(
      `/v2/${companyId}/periodic_review/${id}`,
    );

    return response.data;
  },
  concludePeriodicReview(companyId: number, documentId: QualioDocument['id']) {
    return medtechApiClient
      .patch<PeriodicReview>(
        `/v2/${companyId}/documents/${documentId}/periodic_review_concluded`,
      )
      .then((res) => {
        return res.data;
      });
  },
  createPeriodicReview(
    currentUser: CurrentUser,
    documentId: QualioDocument['id'],
    data: CreatePeriodicReviewRequest,
  ) {
    return medtechApiClient
      .post<PeriodicReview>(
        `/v2/${currentUser.companyId}/documents/${documentId}/periodic_review`,
        data,
      )
      .then((res) => {
        return res.data;
      });
  },
  approvePeriodicReview(
    currentUser: CurrentUser,
    documentId: QualioDocument['id'],
    data: UserCredentialsPayload,
  ) {
    return medtechApiClient
      .patch<{
        status: string;
      }>(
        `/v2/${currentUser.companyId}/documents/${documentId}/periodic_review`,
        { ...data, approved: true },
      )
      .then(({ data }) => data);
  },
  declinePeriodicReview(
    currentUser: CurrentUser,
    documentId: QualioDocument['id'],
    data: UserCredentialsPayload,
  ) {
    return medtechApiClient
      .patch<{
        status: string;
      }>(
        `/v2/${currentUser.companyId}/documents/${documentId}/periodic_review`,
        { ...data, approved: false },
      )
      .then(({ data }) => data);
  },
  getDocumentVersionsByDocumentId(
    documentId: QualioDocument['id'],
    options: { include_minor?: boolean } = {},
  ) {
    const { include_minor = true } = options;
    return medtechApiClient
      .get<DocumentVersion[]>(`/documents/${documentId}/versions`, {
        params: {
          include_minor,
        },
        paramsSerializer: {
          indexes: null,
        },
      })
      .then(({ data }) => data);
  },
  async undoDelete(id: string, version: string): Promise<QualioDocument> {
    const data = {
      id: version,
      status: 'draft',
    };

    return medtechApiClient
      .put(`/documents/${id}/versions/${version}`, data)
      .then((res) => res.data);
  },
  updateDocumentApprovers(
    companyId: number,
    documentId: QualioDocument['id'],
    data: {
      addedUsercompanyIds?: MedtechUserV2['usercompany_id'][];
      removedUsercompanyIds?: MedtechUserV2['usercompany_id'][];
      comment?: string;
    },
  ) {
    const {
      addedUsercompanyIds = [],
      removedUsercompanyIds = [],
      comment,
    } = data;
    return medtechApiClient
      .post<void>(`/v3/${companyId}/documents/${documentId}/manage-approvers`, {
        added_usercompany_ids: addedUsercompanyIds,
        removed_usercompany_ids: removedUsercompanyIds,
        comment,
      })
      .then(({ data }) => data);
  },
  updateDocumentReviewers(
    companyId: number,
    documentId: QualioDocument['id'],
    data: {
      addedUsercompanyIds?: MedtechUserV2['usercompany_id'][];
      removedUsercompanyIds?: MedtechUserV2['usercompany_id'][];
      comment?: string;
    },
  ) {
    const {
      addedUsercompanyIds = [],
      removedUsercompanyIds = [],
      comment,
    } = data;
    return medtechApiClient
      .post<void>(`/${companyId}/documents/${documentId}/manage-reviewers`, {
        added_usercompany_ids: addedUsercompanyIds,
        removed_usercompany_ids: removedUsercompanyIds,
        comment,
      })
      .then(({ data }) => data);
  },
  async signOffTraining(
    documentId: QualioDocument['id'],
    email: string,
    password: string,
    assessment?: TrainingAssessmentValidation,
  ) {
    const payload: any = {
      email,
      password,
      ...assessment,
    };

    return medtechApiClient.put(`/documents/${documentId}/train`, payload);
  },

  async validateAssessment(
    documentId: QualioDocument['id'],
    assessmentToValidate: TrainingAssessmentValidation,
  ) {
    return medtechApiClient.put(
      `/documents/${documentId}/assessment/validate`,
      assessmentToValidate,
    );
  },

  async fetchActivityLogs(
    companyId: CurrentUser['companyId'],
    documentId: QualioDocument['id'],
    actionType: GeneralActionTypeFilter,
  ): Promise<ActivityLog[]> {
    return medtechApiClient
      .get(
        `v2/${companyId}/documents/${documentId}/activity?actionType=${actionType}`,
      )
      .then((res) => res.data.data);
  },

  async completeControlledExport(
    companyId: CurrentUser['companyId'],
    documentId: QualioDocument['id'],
  ): Promise<ActivityLog[]> {
    return medtechApiClient.post(
      `v2/${companyId}/documents/${documentId}/controlled-export-complete`,
    );
  },

  async regenerateAttachmentPreview(attachmentId: string): Promise<void> {
    return medtechApiClient.post(`/preview/${attachmentId}`);
  },
};

type QualioAttachmentPreviewState =
  | 'n/a'
  | 'waiting'
  | 'processing'
  | 'done'
  | 'failed';

export type QualioAttachmentPreviewType = 'document' | 'image' | 'unsupported';

export type QualioAttachmentInfoResponse = {
  id: number;
  filename: string;
  date_added: number;
  external_file_last_fetched_at: number | null;
  updated_at: number | null;
  preview_state: QualioAttachmentPreviewState | null;
  preview_type: QualioAttachmentPreviewType | null;
  external_file_download_url: QualioDocumentAttachment['external_file_download_url'];
  external_file_source_type: QualioDocumentAttachment['external_file_source_type'];
  external_file_url: QualioDocumentAttachment['external_file_url'];
};
